import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, AlertTriangle, XCircle, ArrowLeft, ShieldCheck } from "lucide-react";

/**
 * Public certification verification page.
 * A carrier / adjuster scans the QR printed on the Proof-of-Loss PDF (or
 * pastes the cert hash into the URL) and sees a valid/revoked/not-found card.
 */
export default function VerifyCertificationPage() {
  const { certHash } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!certHash) return;
    api
      .get(`/verify/${certHash}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Verification failed"))
      .finally(() => setLoading(false));
  }, [certHash]);

  const renderCard = () => {
    if (loading) return <p className="text-gray-500">Verifying signature…</p>;
    if (error)
      return (
        <div className="flex items-start gap-3 text-red-700">
          <XCircle className="w-6 h-6 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Lookup failed</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      );

    if (!data)
      return <p className="text-gray-500">No data returned.</p>;

    const icon = data.valid ? (
      <CheckCircle2 className="w-10 h-10 text-emerald-600 shrink-0" />
    ) : data.status === "revoked" ? (
      <AlertTriangle className="w-10 h-10 text-amber-600 shrink-0" />
    ) : (
      <XCircle className="w-10 h-10 text-red-600 shrink-0" />
    );
    const label = data.valid
      ? "Valid signature"
      : data.status === "revoked"
        ? "Revoked"
        : "Not found";
    const color = data.valid
      ? "text-emerald-700"
      : data.status === "revoked"
        ? "text-amber-700"
        : "text-red-700";

    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          {icon}
          <div className="flex-1">
            <p className={`text-2xl font-['Outfit'] font-semibold ${color}`} data-testid="verify-status-label">
              {label}
            </p>
            {data.valid && (
              <p className="text-sm text-gray-600 mt-1">
                This Proof of Loss was electronically signed on ClaimScope under
                the U.S. E-SIGN Act.
              </p>
            )}
            {data.status === "revoked" && (
              <p className="text-sm text-gray-600 mt-1">
                This certification has been revoked and is no longer considered valid.
              </p>
            )}
            {data.status === "not_found" && (
              <p className="text-sm text-gray-600 mt-1">
                No record of this certification hash. It may have been tampered
                with, or the hash was copied incorrectly.
              </p>
            )}
          </div>
        </div>

        {data.valid || data.status === "revoked" ? (
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
            <div>
              <dt className="text-xs text-gray-500">Signer (legal name)</dt>
              <dd className="font-medium text-[#1E3F33]" data-testid="verify-legal-name">
                {data.legal_name}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Typed signature</dt>
              <dd className="font-['Times'] italic text-lg text-[#1E3F33]" data-testid="verify-typed-name">
                /s/ {data.typed_name}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Signed at (UTC)</dt>
              <dd className="font-mono text-[#1E3F33]" data-testid="verify-signed-at">
                {data.signed_at?.slice(0, 19)?.replace("T", " ")}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Claim reference</dt>
              <dd className="font-mono text-[#1E3F33]">{data.claim_id_short}…</dd>
            </div>
            {data.revoked_at && (
              <>
                <div>
                  <dt className="text-xs text-gray-500">Revoked at</dt>
                  <dd className="font-mono text-[#1E3F33]">
                    {data.revoked_at?.slice(0, 19)?.replace("T", " ")}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Revoked by</dt>
                  <dd className="font-medium capitalize text-[#1E3F33]">
                    {data.revoked_by_role}
                  </dd>
                </div>
              </>
            )}
          </dl>
        ) : null}

        <div className="rounded-md border border-[#E5E7EB] bg-[#F9F8F6] p-3 text-xs text-gray-600">
          <span className="text-gray-500">Audit hash (SHA-256): </span>
          <span className="font-mono break-all" data-testid="verify-cert-hash">
            {data.cert_hash}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-2xl mx-auto px-6 py-12" data-testid="verify-page">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-600 hover:text-[#1E3F33] mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> ClaimScope home
        </Link>

        <div className="flex items-start gap-3 mb-6">
          <ShieldCheck className="w-7 h-7 text-[#C25934] shrink-0 mt-1" />
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C25934] font-medium mb-1">
              Public verification
            </p>
            <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight">
              Verify Proof-of-Loss signature
            </h1>
            <p className="text-gray-600 mt-1 text-sm">
              Confirms that a Sworn Proof of Loss was electronically signed on ClaimScope.
            </p>
          </div>
        </div>

        <Card className="border-[#E5E7EB]">
          <CardContent className="p-8">{renderCard()}</CardContent>
        </Card>
      </div>
    </div>
  );
}
